import { create } from 'zustand';
import { type CustomRoleEditableField } from '../utils/chatEditableObjectUtils';
import {
  getResolvedObjectUpdateFields,
  markObjectUpdateFieldsResolved,
  markObjectUpdatesConsumed,
} from './consumedObjectUpdatesStore';

export type { CustomRoleEditableField };

export type PendingCustomRoleFieldEdit = {
  field: CustomRoleEditableField;
  before: string;
  after: string;
  sourceUpdateId?: string;
};

export type PendingCustomRoleEdit = {
  fields: PendingCustomRoleFieldEdit[];
  reason?: string;
  timestamp: number;
};

type CustomRoleDraftFields = {
  name: string;
  description: string;
};

const CUSTOM_ROLE_EDITABLE_FIELD_KEYS: CustomRoleEditableField[] = [
  'name',
  'description',
];

const defaultDraftFields: CustomRoleDraftFields = {
  name: '',
  description: '',
};

function isCustomRoleEditableField(
  key: string,
): key is CustomRoleEditableField {
  return CUSTOM_ROLE_EDITABLE_FIELD_KEYS.includes(
    key as CustomRoleEditableField,
  );
}

function normalizePatch(
  patch: Record<string, unknown>,
): Partial<CustomRoleDraftFields> {
  const entries = CUSTOM_ROLE_EDITABLE_FIELD_KEYS.flatMap((key) => {
    const value = patch[key];
    if (value === undefined) {
      return [];
    }
    return [[key, typeof value === 'string' ? value : String(value)] as const];
  });
  return Object.fromEntries(entries) as Partial<CustomRoleDraftFields>;
}

function mergePendingFieldEdits({
  existing,
  patch,
  baseFields,
  sourceUpdateId,
}: {
  existing: PendingCustomRoleFieldEdit[];
  patch: Partial<CustomRoleDraftFields>;
  baseFields?: CustomRoleDraftFields;
  sourceUpdateId?: string;
}): PendingCustomRoleFieldEdit[] {
  const byField = new Map<CustomRoleEditableField, PendingCustomRoleFieldEdit>(
    existing.map((entry) => [entry.field, entry]),
  );

  for (const [key, after] of Object.entries(patch)) {
    if (!isCustomRoleEditableField(key) || after === undefined) {
      continue;
    }
    const before = baseFields?.[key] ?? '';
    if (before === after) {
      byField.delete(key);
      continue;
    }
    byField.set(key, {
      field: key,
      before,
      after,
      sourceUpdateId,
    });
  }

  return [...byField.values()];
}

function uniqueSourceUpdateIds(ids: Array<string | undefined>): string[] {
  return [
    ...new Set(
      ids.filter((id): id is string => typeof id === 'string' && id.length > 0),
    ),
  ];
}

function collectSourceUpdateIds(
  entries: PendingCustomRoleFieldEdit[],
): string[] {
  return uniqueSourceUpdateIds(entries.map((entry) => entry.sourceUpdateId));
}

function filterFullyResolvedSourceUpdateIds({
  resolvedEntries,
  remainingEntries,
}: {
  resolvedEntries: PendingCustomRoleFieldEdit[];
  remainingEntries: PendingCustomRoleFieldEdit[];
}): string[] {
  const resolvedSourceUpdateIds = collectSourceUpdateIds(resolvedEntries);
  const remainingSourceUpdateIds = new Set(
    collectSourceUpdateIds(remainingEntries),
  );
  return resolvedSourceUpdateIds.filter(
    (sourceUpdateId) => !remainingSourceUpdateIds.has(sourceUpdateId),
  );
}

type RoleEditStore = {
  draftFieldsByRoleId: Record<string, CustomRoleDraftFields>;
  pendingAiEdits: Record<string, PendingCustomRoleEdit>;
  stageAiPatch: (
    roleId: string,
    {
      patch,
      reason,
      baseFields,
      sourceUpdateId,
    }: {
      patch: Record<string, unknown>;
      reason?: string;
      baseFields?: CustomRoleDraftFields;
      sourceUpdateId?: string;
    },
  ) => void;
  acceptAiEdits: (
    roleId: string,
    fields?: CustomRoleEditableField[],
    options?: { baseDraftFields: CustomRoleDraftFields },
  ) => CustomRoleDraftFields | undefined;
  discardAiEdits: (roleId: string, fields?: CustomRoleEditableField[]) => void;
  getPendingAiEdits: (roleId: string) => PendingCustomRoleEdit | undefined;
  listPendingSourceUpdateIds: (roleId: string) => string[];
  getDraftFields: (roleId: string) => CustomRoleDraftFields | undefined;
  syncRoleEditBaseFields: (
    roleId: string,
    baseFields: CustomRoleDraftFields,
  ) => void;
  clearRoleEditState: (roleId: string) => void;
  clearPendingEditsForSourceUpdateIds: (sourceUpdateIds: string[]) => void;
  clearAllPendingRoleEdits: () => void;
};

export const useRoleEditStore = create<RoleEditStore>((set, get) => ({
  draftFieldsByRoleId: {},
  pendingAiEdits: {},

  stageAiPatch: (roleId, { patch, reason, baseFields, sourceUpdateId }) => {
    const normalizedPatch = normalizePatch(patch);
    if (sourceUpdateId) {
      const resolvedFields = getResolvedObjectUpdateFields(sourceUpdateId);
      for (const field of Object.keys(normalizedPatch)) {
        if (resolvedFields.has(field)) {
          delete normalizedPatch[field as CustomRoleEditableField];
        }
      }
    }

    if (Object.keys(normalizedPatch).length === 0) {
      return;
    }

    const existingPending = get().pendingAiEdits[roleId];
    const storedDraft = get().draftFieldsByRoleId[roleId];
    const resolvedBaseFields = baseFields ?? storedDraft;
    const mergeBaseFields = resolvedBaseFields ?? defaultDraftFields;
    const nextFields = mergePendingFieldEdits({
      existing: existingPending?.fields ?? [],
      patch: normalizedPatch,
      baseFields: mergeBaseFields,
      sourceUpdateId,
    });

    if (nextFields.length === 0) {
      return;
    }

    set((state) => {
      const nextDraftFieldsByRoleId = { ...state.draftFieldsByRoleId };
      if (resolvedBaseFields) {
        nextDraftFieldsByRoleId[roleId] = resolvedBaseFields;
      }

      return {
        pendingAiEdits: {
          ...state.pendingAiEdits,
          [roleId]: {
            fields: nextFields,
            reason: reason ?? existingPending?.reason,
            timestamp: Date.now(),
          },
        },
        draftFieldsByRoleId: nextDraftFieldsByRoleId,
      };
    });
  },

  syncRoleEditBaseFields: (roleId, baseFields) => {
    const pending = get().pendingAiEdits[roleId];
    const existingDraft = get().draftFieldsByRoleId[roleId];
    const draftUnchanged =
      existingDraft?.name === baseFields.name &&
      existingDraft?.description === baseFields.description;

    if (!pending) {
      if (draftUnchanged || existingDraft) {
        return;
      }
      set((state) => ({
        draftFieldsByRoleId: {
          ...state.draftFieldsByRoleId,
          [roleId]: baseFields,
        },
      }));
      return;
    }

    const refreshedFields = pending.fields.map((entry) => ({
      ...entry,
      before: baseFields[entry.field],
    }));
    const pendingUnchanged = refreshedFields.every(
      (entry, index) =>
        entry.before === pending.fields[index]?.before &&
        entry.after === pending.fields[index]?.after &&
        entry.field === pending.fields[index]?.field,
    );

    if (pendingUnchanged && draftUnchanged) {
      return;
    }

    set((state) => {
      const nextPendingAiEdits = { ...state.pendingAiEdits };
      if (refreshedFields.length === 0) {
        delete nextPendingAiEdits[roleId];
      } else {
        nextPendingAiEdits[roleId] = {
          ...pending,
          fields: refreshedFields,
        };
      }

      return {
        pendingAiEdits: nextPendingAiEdits,
        draftFieldsByRoleId: {
          ...state.draftFieldsByRoleId,
          [roleId]: baseFields,
        },
      };
    });
  },

  acceptAiEdits: (roleId, fields, options) => {
    const pending = get().pendingAiEdits[roleId];
    if (!pending || !options?.baseDraftFields) {
      return undefined;
    }

    const fieldsToAccept = fields
      ? pending.fields.filter((entry) => fields.includes(entry.field))
      : pending.fields;
    if (fieldsToAccept.length === 0) {
      return undefined;
    }

    const nextDraftFields = { ...options.baseDraftFields };
    for (const entry of fieldsToAccept) {
      nextDraftFields[entry.field] = entry.after;
    }

    const remainingFields = pending.fields.filter(
      (entry) =>
        !fieldsToAccept.some((accepted) => accepted.field === entry.field),
    );

    const sourceUpdateIds = [
      ...new Set(
        fieldsToAccept
          .map((entry) => entry.sourceUpdateId)
          .filter(
            (id): id is string => typeof id === 'string' && id.length > 0,
          ),
      ),
    ];
    if (sourceUpdateIds.length > 0) {
      markObjectUpdatesConsumed(sourceUpdateIds);
      for (const sourceUpdateId of sourceUpdateIds) {
        markObjectUpdateFieldsResolved({
          sourceUpdateId,
          fields: fieldsToAccept
            .filter((entry) => entry.sourceUpdateId === sourceUpdateId)
            .map((entry) => entry.field),
        });
      }
    }

    set((state) => {
      const nextPendingAiEdits = { ...state.pendingAiEdits };
      if (remainingFields.length === 0) {
        delete nextPendingAiEdits[roleId];
      } else {
        nextPendingAiEdits[roleId] = {
          ...pending,
          fields: remainingFields,
        };
      }

      return {
        pendingAiEdits: nextPendingAiEdits,
        draftFieldsByRoleId: {
          ...state.draftFieldsByRoleId,
          [roleId]: nextDraftFields,
        },
      };
    });

    return nextDraftFields;
  },

  discardAiEdits: (roleId, fields) => {
    const pending = get().pendingAiEdits[roleId];
    if (!pending) {
      return;
    }

    const discardedEntries = fields
      ? pending.fields.filter((entry) => fields.includes(entry.field))
      : pending.fields;

    const remainingFields = fields
      ? pending.fields.filter((entry) => !fields.includes(entry.field))
      : [];

    const consumedSourceUpdateIds = filterFullyResolvedSourceUpdateIds({
      resolvedEntries: discardedEntries,
      remainingEntries: remainingFields,
    });
    if (consumedSourceUpdateIds.length > 0) {
      markObjectUpdatesConsumed(consumedSourceUpdateIds);
    }

    set((state) => {
      const nextPendingAiEdits = { ...state.pendingAiEdits };
      if (!fields || remainingFields.length === 0) {
        delete nextPendingAiEdits[roleId];
      } else {
        nextPendingAiEdits[roleId] = {
          ...pending,
          fields: remainingFields,
        };
      }
      return { pendingAiEdits: nextPendingAiEdits };
    });
  },

  getPendingAiEdits: (roleId) => get().pendingAiEdits[roleId],

  listPendingSourceUpdateIds: (roleId) => {
    const pending = get().pendingAiEdits[roleId];
    if (!pending) {
      return [];
    }
    return [
      ...new Set(
        pending.fields
          .map((entry) => entry.sourceUpdateId)
          .filter(
            (id): id is string => typeof id === 'string' && id.length > 0,
          ),
      ),
    ];
  },

  getDraftFields: (roleId) => get().draftFieldsByRoleId[roleId],

  clearRoleEditState: (roleId) => {
    set((state) => {
      const { [roleId]: _draft, ...draftFieldsByRoleId } =
        state.draftFieldsByRoleId;
      const { [roleId]: _pending, ...pendingAiEdits } = state.pendingAiEdits;
      return { draftFieldsByRoleId, pendingAiEdits };
    });
  },

  clearPendingEditsForSourceUpdateIds: (sourceUpdateIds) => {
    if (sourceUpdateIds.length === 0) {
      return;
    }

    const idsToClear = new Set(sourceUpdateIds);
    set((state) => {
      const nextPendingAiEdits = { ...state.pendingAiEdits };
      let changed = false;

      for (const [roleId, pending] of Object.entries(nextPendingAiEdits)) {
        const remainingFields = pending.fields.filter(
          (entry) =>
            !entry.sourceUpdateId || !idsToClear.has(entry.sourceUpdateId),
        );
        if (remainingFields.length === pending.fields.length) {
          continue;
        }

        changed = true;
        if (remainingFields.length === 0) {
          delete nextPendingAiEdits[roleId];
        } else {
          nextPendingAiEdits[roleId] = {
            ...pending,
            fields: remainingFields,
          };
        }
      }

      return changed ? { pendingAiEdits: nextPendingAiEdits } : state;
    });
  },

  clearAllPendingRoleEdits: () => {
    set({ pendingAiEdits: {} });
  },
}));
